import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useTheme } from '../../styles/ThemeProvider';

/**
 * One selectable option row (GET-266), Back Market style: name and an optional detail line
 * on the left, an already-formatted value on the right. Selection is a 2px textPrimary
 * border with padding trimmed by 1, so the row keeps its size.
 *
 * `value`: a string renders as is; `null` (or `unknown`) renders "—"; omitting the prop
 * leaves out the value lane entirely (e.g. VariantPicker with `showPrices={false}`).
 */
export interface OptionRowProps {
  name: string;
  /** One line, sm, textSecondary. */
  detail?: string;
  value?: string | null;
  /** Whole row in textTertiary, value "—". */
  unknown?: boolean;
  selected: boolean;
  onPress: () => void;
  testID?: string;
}

const PAD_V = 12;
const PAD_H = 16;
export const OPTION_ROW_EMPTY_VALUE = '—';

export function OptionRow({
  name,
  detail,
  value,
  unknown,
  selected,
  onPress,
  testID,
}: OptionRowProps) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        row: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
          borderRadius: theme.radius.radiusMd,
          borderWidth: 1,
          borderColor: theme.colors.border,
          paddingVertical: PAD_V,
          paddingHorizontal: PAD_H,
        },
        rowSelected: {
          borderWidth: 2,
          borderColor: theme.colors.textPrimary,
          paddingVertical: PAD_V - 1,
          paddingHorizontal: PAD_H - 1,
        },
        text: { flex: 1, gap: theme.spacing.xxs },
        name: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        detail: { ...theme.typography.sm, color: theme.colors.textSecondary },
        // Plex Mono at md size (valueSm's family, md's 15/23 metrics).
        value: {
          fontFamily: theme.typography.valueSm.fontFamily,
          fontSize: theme.typography.md.fontSize,
          lineHeight: theme.typography.md.lineHeight,
          color: theme.colors.textPrimary,
          textAlign: 'right',
        },
        muted: { color: theme.colors.textTertiary },
      }),
    [theme]
  );

  const showValue = unknown || value !== undefined;
  const shownValue = unknown || value == null ? OPTION_ROW_EMPTY_VALUE : value;
  // RN-web emits no aria-selected for role="button", so on web the selected row says so in its
  // label (VariantPicker's current variant). Native platforms announce accessibilityState.selected.
  const webSelectedLabel =
    Platform.OS === 'web' && selected
      ? [name, detail, showValue ? shownValue : null, 'selected'].filter(Boolean).join(', ')
      : undefined;

  return (
    <Pressable
      style={[styles.row, selected && styles.rowSelected]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      accessibilityLabel={webSelectedLabel}
      testID={testID}
    >
      <View style={styles.text}>
        <Text style={[styles.name, unknown && styles.muted]}>{name}</Text>
        {detail ? (
          <Text
            style={[styles.detail, unknown && styles.muted]}
            numberOfLines={1}
            testID={testID ? `${testID}-detail` : undefined}
          >
            {detail}
          </Text>
        ) : null}
      </View>
      {showValue && (
        <Text
          style={[styles.value, unknown && styles.muted]}
          testID={testID ? `${testID}-value` : undefined}
        >
          {shownValue}
        </Text>
      )}
    </Pressable>
  );
}

export default OptionRow;
